import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CreditCard, Lock, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { DiscountLines, FreeShippingBar } from "@/components/CartDrawer";
import { api, errMsg, eur, img } from "@/lib/api";

export const submitRedsys = ({ action, fields }) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  Object.entries(fields).forEach(([name, value]) => {
    const i = document.createElement("input");
    i.type = "hidden"; i.name = name; i.value = value;
    form.appendChild(i);
  });
  document.body.appendChild(form);
  form.submit();
};

const FIELDS = [["name", "Nombre y apellidos", "sm:col-span-2"], ["address", "Dirección", "sm:col-span-2"], ["postal_code", "Código postal", ""], ["city", "Población", ""], ["province", "Provincia", ""], ["phone", "Teléfono", ""]];

const Method = ({ value, current, onChange, icon: Icon, title, text }) => (
  <button type="button" onClick={() => onChange(value)} data-testid={`payment-method-${value}`}
    className={`flex items-start gap-3 text-left p-4 rounded border-2 transition-colors ${current === value ? "border-[#0B1D3A] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#93C5FD]"}`}>
    <Icon className={current === value ? "text-[#DC2626]" : "text-slate-400"} size={22} />
    <div><p className="font-display font-bold text-lg text-[#0B1D3A] leading-none">{title}</p><p className="text-xs text-slate-500 mt-1">{text}</p></div>
  </button>
);

export default function Checkout() {
  const { user } = useAuth();
  const { items, subtotal, shipping, total } = useCart();
  const [addr, setAddr] = useState({ name: "", address: "", city: "", postal_code: "", province: "", phone: "" });
  const [method, setMethod] = useState("card");
  const [gift, setGift] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user?.address) setAddr(user.address); else if (user) setAddr((a) => ({ ...a, name: user.name })); }, [user]);

  if (user === null) return <div className="py-32 text-center text-slate-500">Cargando…</div>;
  if (user === false) return <Navigate to="/login?next=/checkout" replace />;
  if (items.length === 0) return (
    <div className="max-w-xl mx-auto py-24 text-center px-4" data-testid="checkout-empty">
      <h1 className="font-display font-black text-4xl text-[#0B1D3A]">Tu carrito está vacío</h1>
      <Link to="/tienda" className="inline-block mt-6 bg-[#DC2626] text-white font-display font-bold px-6 py-3 rounded-full">Ir a la tienda</Link>
    </div>
  );

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { items: items.map(({ slug, format_id, qty }) => ({ slug, format_id, qty })), shipping_address: addr, payment_method: method, gift });
      localStorage.setItem("faunny_pending_order", data.order_id);
      submitRedsys(data.redsys);
    } catch (e2) {
      toast.error(errMsg(e2));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={pay} className="max-w-7xl mx-auto px-4 py-12 grid lg:grid-cols-[1.3fr_1fr] gap-10" data-testid="checkout-page">
      <div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A]">Finalizar compra</h1>
        <h2 className="font-display font-bold text-2xl text-[#0B1D3A] mt-10">1. Datos de envío</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {FIELDS.map(([k, l, cls]) => (
            <div key={k} className={cls}>
              <Label htmlFor={k}>{l}</Label>
              <Input id={k} required value={addr[k] || ""} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} data-testid={`checkout-${k}-input`} className="mt-1.5 h-11" />
            </div>
          ))}
        </div>
        <label className="mt-5 flex items-start gap-3 border border-slate-200 rounded p-4 cursor-pointer hover:border-[#93C5FD]" data-testid="checkout-gift-label">
          <input type="checkbox" checked={gift} onChange={(e) => setGift(e.target.checked)} className="mt-1 accent-[#DC2626] w-4 h-4" data-testid="checkout-gift-checkbox" />
          <span className="text-sm"><b className="text-[#0B1D3A]">Es un regalo</b><br /><span className="text-slate-600">Enviaremos el paquete sin precios en el albarán.</span></span>
        </label>
        <h2 className="font-display font-bold text-2xl text-[#0B1D3A] mt-10">2. Método de pago</h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Method value="card" current={method} onChange={setMethod} icon={CreditCard} title="Tarjeta" text="Visa, Mastercard y más" />
          <Method value="bizum" current={method} onChange={setMethod} icon={Smartphone} title="Bizum" text="Paga desde tu móvil" />
        </div>
        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Lock size={13} /> Serás redirigido a la página segura de Redsys para completar el pago. Entorno de pruebas: no se cobra dinero real.</p>
      </div>
      <aside className="bg-[#EFF6FF]/60 border border-slate-200 rounded p-6 h-fit lg:sticky lg:top-28">
        <h2 className="font-display font-bold text-2xl text-[#0B1D3A]">Tu pedido</h2>
        <div className="mt-4 divide-y divide-slate-200">
          {items.map((it) => (
            <div key={`${it.slug}-${it.format_id}`} className="flex gap-3 py-3 items-center">
              <img src={img(it.image)} alt="" className="w-14 h-14 object-contain bg-white rounded" />
              <div className="flex-1 text-sm"><p className="font-semibold">{it.name}</p><p className="text-slate-500 text-xs">{it.format_label} × {it.qty}</p></div>
              <span className="text-sm font-bold">{eur(it.price * it.qty)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4"><FreeShippingBar /></div>
        <div className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{eur(subtotal)}</span></div>
          <DiscountLines />
          <div className="flex justify-between"><span>Envío</span><span data-testid="checkout-shipping">{shipping ? eur(shipping) : "Gratis"}</span></div>
          <div className="flex justify-between text-lg font-bold pt-2"><span>Total <span className="text-xs font-normal text-slate-500">IVA incl.</span></span><span className="text-[#DC2626]" data-testid="checkout-total">{eur(total)}</span></div>
        </div>
        <label className="flex items-start gap-2.5 text-sm mt-4 cursor-pointer">
          <input type="checkbox" required className="mt-0.5 accent-[#DC2626] w-4 h-4" data-testid="checkout-accept-terms"
            onInvalid={(e) => e.target.setCustomValidity("Debes aceptar las condiciones de venta para continuar")} onChange={(e) => e.target.setCustomValidity("")} />
          <span>He leído y acepto las <Link to="/legal/condiciones-de-venta" target="_blank" className="text-[#DC2626] font-bold underline">condiciones de venta</Link> y la <Link to="/legal/privacidad" target="_blank" className="text-[#DC2626] font-bold underline">política de privacidad</Link>.</span>
        </label>
        <button disabled={busy} data-testid="checkout-pay-button" className="w-full mt-5 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white font-display font-bold text-lg py-3.5 rounded-full transition-colors">
          {busy ? "Redirigiendo a Redsys…" : `Pagar ${eur(total)} con ${method === "bizum" ? "Bizum" : "tarjeta"}`}
        </button>
      </aside>
    </form>
  );
}
